// frontend/src/context/AppContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/axios';
import { toast } from 'react-toastify';

export const AppContext = createContext();

export const useAppContext = () => {
    return useContext(AppContext);
};

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    
    // --- STATE SETTINGS ---
    const [settings, setSettings] = useState(null);       // ID 1: General Info (Nama Event, Tgl, dll)
    const [masterData, setMasterData] = useState(null);   // ID 2: Master Data (Kelas, Divisi, Harga)
    const [idCardConfig, setIdCardConfig] = useState(null); // ID 3: Template ID Card (Warna, BG)
    
    const [isLoading, setIsLoading] = useState(true);

    // Helper: Parse Config aman (Handle jika string JSON atau sudah Object)
    const parseConfig = (data) => {
        if (!data || !data.config) return {};
        return typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
    };

    // Fungsi untuk mengambil semua pengaturan
    const loadAllSettings = async () => {
        try {
            const [resInfo, resMaster, resIdCard] = await Promise.all([
                api.get('/settings/1'),
                api.get('/settings/2'),
                api.get('/settings/3').catch(() => ({ data: null })) // Ignore error jika ID 3 belum ada
            ]);

            // Set ID 1 (Settings Umum)
            setSettings(parseConfig(resInfo.data));
            
            // Set ID 2 (Master Data)
            setMasterData(parseConfig(resMaster.data));

            // Set ID 3 (ID Card Template)
            if (resIdCard.data) {
                setIdCardConfig(parseConfig(resIdCard.data));
            } else {
                setIdCardConfig(null);
            }
            
        } catch (error) {
            console.error("Gagal memuat pengaturan:", error);
            // Fallback agar aplikasi tidak crash
            setSettings({});
            setMasterData({});
        }
    };

    useEffect(() => {
        const checkAuthAndSettings = async () => {
            setIsLoading(true);
            try {
                // Jalankan fetch data user dan seluruh settings secara bersamaan (Parallel)
                await Promise.all([
                    (async () => {
                        try {
                            const response = await api.get('/auth/me');
                            setUser(response.data.user);
                        } catch (error) {
                            setUser(null);
                        }
                    })(),
                    loadAllSettings()
                ]);
            } catch (error) {
                console.error("Error during initial data fetch", error);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuthAndSettings();
    }, []);

    const handleLogin = (userData) => {
        setUser(userData);
        toast.success(`Selamat datang, ${userData.name || 'User'}!`);
    };

    const handleLogout = async () => {
        try {
            await api.delete('/auth/logout');
            setUser(null);
            toast.info("Anda telah keluar.");
        } catch (error) {
            console.error("Logout gagal", error);
        }
    };

    const value = {
        user,
        settings,       // ID 1
        masterData,     // ID 2
        idCardConfig,   // ID 3 (Penting untuk TeamDetail.jsx)
        isLoading,
        login: handleLogin,
        logout: handleLogout,
        reloadSettings: loadAllSettings // Panggil ini jika Admin mengupdate setting, agar frontend refresh tanpa reload page
    };

    return (
        <AppContext.Provider value={value}>
            {!isLoading && children}
        </AppContext.Provider>
    );
};